/**
 * FO-473 (batch-63) — de beslissing per dagregel in de pauze-herrekening.
 *
 * Puur: de service (`apps/api/src/lib/werkbon-pauze-herstel.ts`) laadt de
 * dagregel, bepaalt of hij nog OPEN is en rekent de cascade uit; deze functie
 * beslist wat er met de dagregel gebeurt. Zo zijn de geld-regels unit-testbaar:
 *
 *   1. Alleen OPEN dagregels (niet verloond, niet gefactureerd, niet in een
 *      facturatieverzoek of -overzicht, geen payroll-lock, dag niet gesloten)
 *      krijgen de nieuwe uitkomst.
 *   2. Een gesloten dagregel waarvan de betaalde of facturabele uren zouden
 *      wijzigen komt op de CORRECTIELIJST (beslispunt PO) en wordt nooit
 *      aangeraakt. Alleen een herkomst-verschil op een gesloten regel is geen
 *      correctie en blijft stil liggen.
 *   3. Een handmatige as blijft staan (per as).
 *   4. Herbeoordeling van oude handmatig-vlaggen (herkomst MIGRATIE): de
 *      batch-56-backfill vlagde elke pauze > 0, en het oude aanmaakformulier
 *      stuurde ALTIJD een pauze mee (standaard 60, of de eerste dienst-pauze
 *      van het werkschema - ook een onbevestigde 0), waardoor elke via dat
 *      formulier gemaakte dagregel de vlag kreeg. Daarom:
 *        - gelijk aan de regel -> de vlag vervalt (geen keuze);
 *        - een 0 die uit een onbevestigde werkschema-0 komt -> vervalt (de
 *          stille 0 die deze herrekening moet repareren);
 *        - een vlag van ná de backfill met een waarde die het oude formulier
 *          stuurde (`oudeFormulierPauzes`) en die afwijkt -> ONZEKER: niet
 *          aangeraakt, apart gerapporteerd (beslispunt PO), alleen herzien met
 *          `herzieAanmaakVlaggen`. Een andere waarde (bv. een monteur die 0
 *          boekte) is een echte keuze;
 *        - anders blijft de vlag staan.
 *   5. Een dagregel zonder eigen factuurpauze (aangemaakt door de vorige
 *      revisie tijdens het deploy-venster) werd gefactureerd met de oude
 *      schema-brede facturatie-pauze als vangnet; de "oude" factuur-uren
 *      rekenen daar dus mee (`oudeFacturatiePauze`).
 */
import {
  betaaldeUren,
  computeWerkHoursFromSegments,
  facturabeleUren,
  type OudeFacturatiePauze,
  type WerkSegment,
} from './werkbon-hours'
import {
  PAUZE_SYSTEEM_DEFAULTS,
  afgeleideFacturatieMinuten,
  bruikbaarPauzeGetal,
  dienstduurMinuten,
  pauzeVeldenGewijzigd,
  vangnetFactuurpauzeMinuten,
  KORTE_DIENST_DREMPEL_MINUTEN,
  type DagregelPauzeStand,
  type DagregelPauzeVelden,
  type PauzeResolutie,
  type PauzeSysteemDefaults,
} from './werkbon-pauze'

/** Waarom een dagregel niet (meer) open is; `OPEN` = herrekenbaar. */
export type HerrekenSlot =
  | 'OPEN'
  | 'DAG_GESLOTEN'
  | 'IN_GOEDKEURING'
  | 'VERLOOND'
  | 'PAYROLL_LOCK'
  | 'GEFACTUREERD'
  | 'FACTURATIEVERZOEK'
  | 'FACTURATIE_OVERZICHT'
  /**
   * Integrator-review M2 (FO-511) - de klant beoordeelt de uren nu of heeft ze
   * geaccordeerd: de factuurpauze gaat niet zonder nieuw akkoord omhoog of omlaag.
   */
  | 'KLANTAKKOORD'

export const HERREKEN_SLOT_LABELS: Record<HerrekenSlot, string> = {
  OPEN: 'open',
  DAG_GESLOTEN: 'dag gesloten (goedgekeurd)',
  IN_GOEDKEURING: 'in de goedkeuring (intern of klant)',
  VERLOOND: 'verwerkt in de verloning',
  PAYROLL_LOCK: 'in een gesloten verloningsperiode',
  GEFACTUREERD: 'gefactureerd',
  FACTURATIEVERZOEK: 'in een facturatieverzoek (Finqle)',
  FACTURATIE_OVERZICHT: 'in een facturatie-overzicht',
  KLANTAKKOORD: 'door de klant geaccordeerd of in beoordeling bij de klant',
}

export interface HerrekenDagregelInput {
  stand: DagregelPauzeStand
  /** Opgeslagen betaalde uren. */
  hours: number
  plannedStart: string
  plannedEnd: string
  actualStart: string | null
  actualEnd: string | null
  werkSegmenten?: WerkSegment[] | null
  datum: Date | string
  /**
   * De cascade voor deze dag zonder handmatige invoer. FO-511: de facturatie-as
   * daarvan komt uitsluitend uit de klantregels, dus hij is dezelfde of de
   * verloningsas nu handmatig blijft of niet.
   */
  regel: PauzeResolutie
  slot: HerrekenSlot
  /**
   * Wat bekend is over een handmatig-vlag met herkomst MIGRATIE (zie punt 4
   * bovenaan): een stille 0 uit een onbevestigd werkschema, of een vlag van ná
   * de batch-56-backfill (oud aanmaakformulier). `null` = niets bekend.
   */
  vlagSignaal?: 'STILLE_NUL_OUD_SCHEMA' | 'AANMAAK_NA_BACKFILL' | null
  /** Vlaggen van het oude aanmaakformulier óók herzien (na akkoord PO). */
  herzieAanmaakVlaggen?: boolean
  /**
   * De oude schema-brede facturatie-pauze van het werkschema van de werkbon:
   * het vangnet waarmee een dagregel zonder eigen factuurpauze (deploy-venster)
   * gefactureerd werd. `null` = geen.
   */
  oudeFacturatiePauze?: OudeFacturatiePauze | null
}

export type HerrekenSoort =
  | 'GEEN_WIJZIGING'
  | 'WIJZIGING'
  | 'CORRECTIELIJST'
  | 'HANDMATIG_BEHOUDEN'
  /** Oude formulier-vlag die afwijkt van de regel: niet aangeraakt, beslispunt PO. */
  | 'HANDMATIG_ONZEKER'

export interface HerrekenDagregelUitkomst {
  soort: HerrekenSoort
  nieuw: DagregelPauzeVelden
  nieuweHours: number
  oudBetaald: number
  nieuwBetaald: number
  oudFactuur: number
  nieuwFactuur: number
  /** Wijzigt er alleen herkomst/vlag (geen minuten, verloning, factuurpauze of uren)? */
  alleenHerkomst: boolean
  /** Herbeoordeling van een MIGRATIE-handmatig-vlag: vervalt, blijft of onzeker. */
  vlagHerbeoordeling: 'VERVALT' | 'BLIJFT' | 'ONZEKER' | null
  /** Verschilt de regel op een as die handmatig blijft? */
  handmatigWijktAf: boolean
  /** De betaalde uren die de FO-423-herrekening (oude regel) zou geven. */
  fo423Betaald: number
  /**
   * FO-511 - de factuurpauze wijkt af van het voorstel, maar de klantregel is nog
   * niet bevestigd: de facturatie-as is NIET gewijzigd (rapporteren, niet schrijven).
   */
  facturatieWachtOpBevestiging: boolean
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/** De tijden van een dagregel waaruit de betaalde uren volgen. */
export interface DagregelTijden {
  plannedStart: string
  plannedEnd: string
  actualStart: string | null
  actualEnd: string | null
  werkSegmenten?: WerkSegment[] | null
}

/**
 * Betaalde uren van een dagregel bij een gegeven verloningsas: werksegmenten
 * als die er zijn (monteur-boeking), anders de tijden. Eén bron voor de
 * herrekening en de backoffice-aanpassing per as.
 */
export function betaaldeUrenVoorPauze(
  input: DagregelTijden,
  pauzeMinutes: number,
  pauzeVerloning: DagregelPauzeVelden['pauzeVerloning'],
): number {
  return urenVoor(input, pauzeMinutes, pauzeVerloning)
}

function urenVoor(
  input: DagregelTijden,
  pauzeMinutes: number,
  pauzeVerloning: DagregelPauzeVelden['pauzeVerloning'],
): number {
  if (input.werkSegmenten && input.werkSegmenten.length > 0) {
    return computeWerkHoursFromSegments(input.werkSegmenten, pauzeVerloning)
  }
  return betaaldeUren({
    plannedStart: input.plannedStart,
    plannedEnd: input.plannedEnd,
    actualStart: input.actualStart,
    actualEnd: input.actualEnd,
    pauzeMinutes,
    pauzeVerloning,
  })
}

export function herrekenDagregelPauze(input: HerrekenDagregelInput): HerrekenDagregelUitkomst {
  const { stand, regel } = input
  const huidigeVerloning = stand.pauzeVerloning ?? 'GESCHREVEN'
  // De factuurpauze zoals hij tot nu toe werkte: de eigen waarde, of (zonder
  // eigen waarde) de afgeleide pauze plus het vangnet van de oude
  // schema-brede facturatie-pauze - precies wat `facturabeleUren` factureerde.
  const oude = input.oudeFacturatiePauze ?? null
  const vangnetMinuten =
    stand.pauzeFacturatieMinuten == null ? vangnetFactuurpauzeMinuten(oude, input.datum, input.hours) : 0
  const effectieveOudeFactuurpauze =
    stand.pauzeFacturatieMinuten ??
    afgeleideFacturatieMinuten(stand.pauzeMinutes, huidigeVerloning) + vangnetMinuten

  // ── Verloningsas ──────────────────────────────────────────────────────────
  let vlagHerbeoordeling: HerrekenDagregelUitkomst['vlagHerbeoordeling'] = null
  let verloningsasHandmatig = stand.pauzeHandmatig
  if (stand.pauzeHandmatig && (stand.pauzeBron == null || stand.pauzeBron === 'MIGRATIE')) {
    if (stand.pauzeMinutes === regel.minuten) {
      // Gelijk aan de regel: nooit een keuze geweest.
      vlagHerbeoordeling = 'VERVALT'
      verloningsasHandmatig = false
    } else if (input.vlagSignaal === 'STILLE_NUL_OUD_SCHEMA' && stand.pauzeMinutes === 0) {
      // De 0 kwam uit een onbevestigd werkschema: precies de stille 0.
      vlagHerbeoordeling = 'VERVALT'
      verloningsasHandmatig = false
    } else if (input.vlagSignaal === 'AANMAAK_NA_BACKFILL') {
      // Oud aanmaakformulier: waarschijnlijk automatisch, maar niet zeker.
      if (input.herzieAanmaakVlaggen) {
        vlagHerbeoordeling = 'VERVALT'
        verloningsasHandmatig = false
      } else {
        vlagHerbeoordeling = 'ONZEKER'
      }
    } else {
      vlagHerbeoordeling = 'BLIJFT'
    }
  }

  const verloningsas = verloningsasHandmatig
    ? {
        pauzeMinutes: stand.pauzeMinutes,
        pauzeVerloning: huidigeVerloning,
        pauzeBron: stand.pauzeBron ?? 'MIGRATIE',
        pauzeVerloningBron: stand.pauzeVerloningBron ?? 'MIGRATIE',
        pauzeHandmatig: true,
      }
    : {
        pauzeMinutes: regel.minuten,
        pauzeVerloning: regel.verloning,
        pauzeBron: regel.bron,
        pauzeVerloningBron: regel.verloningBron,
        pauzeHandmatig: false,
      }

  // ── Facturatie-as ─────────────────────────────────────────────────────────
  // FO-511 - de klantregel, los van de verloningsas. Een handmatige factuurpauze
  // (kantoor, met reden) blijft staan. Een ONBEVESTIGDE klantregel (het voorstel
  // van de systeemstandaard, of een afspraak op een niet-bevestigd werkschema)
  // schrijft de herrekening nooit: dan blijft de factuurpauze waarmee de dagregel
  // gefactureerd werd staan tot het werkschema bevestigd is - nooit een factuur
  // die op een aanname verandert.
  const facturatieBehouden = stand.pauzeHandmatigFacturatie || !regel.facturatieBevestigd
  const facturatieWachtOpBevestiging =
    !stand.pauzeHandmatigFacturatie &&
    !regel.facturatieBevestigd &&
    effectieveOudeFactuurpauze !== regel.facturatieMinuten
  const facturatieas = facturatieBehouden
    ? {
        pauzeFacturatieMinuten: effectieveOudeFactuurpauze,
        pauzeFacturatieBron:
          stand.pauzeFacturatieBron ?? (stand.pauzeHandmatigFacturatie ? 'EXPLICIET' : 'MIGRATIE'),
        pauzeHandmatigFacturatie: stand.pauzeHandmatigFacturatie,
        pauzeFacturatieReden: stand.pauzeHandmatigFacturatie ? (stand.pauzeFacturatieReden ?? null) : null,
      }
    : {
        pauzeFacturatieMinuten: regel.facturatieMinuten,
        pauzeFacturatieBron: regel.facturatieBron,
        pauzeHandmatigFacturatie: false,
        pauzeFacturatieReden: null,
      }

  const nieuw: DagregelPauzeVelden = { ...verloningsas, ...facturatieas }

  const verloningsasGewijzigd =
    nieuw.pauzeMinutes !== stand.pauzeMinutes || nieuw.pauzeVerloning !== huidigeVerloning
  const nieuweHours = verloningsasGewijzigd
    ? urenVoor(input, nieuw.pauzeMinutes, nieuw.pauzeVerloning)
    : input.hours

  const oudBetaald = round2(input.hours)
  const nieuwBetaald = round2(nieuweHours)
  const oudFactuur = facturabeleUren(
    {
      hours: input.hours,
      date: input.datum,
      pauzeMinutes: stand.pauzeMinutes,
      pauzeVerloning: stand.pauzeVerloning,
      pauzeFacturatieMinuten: stand.pauzeFacturatieMinuten,
    },
    oude,
  )
  const nieuwFactuur = facturabeleUren({
    hours: nieuweHours,
    date: input.datum,
    pauzeMinutes: nieuw.pauzeMinutes,
    pauzeVerloning: nieuw.pauzeVerloning,
    pauzeFacturatieMinuten: nieuw.pauzeFacturatieMinuten,
  })

  const geldVerschil = nieuwBetaald !== oudBetaald || nieuwFactuur !== oudFactuur
  // Een lege factuurpauze (van vóór FO-473) volgde de verloning (plus het
  // vangnet); die invullen met precies die waarde is alleen herkomst.
  const waardenGewijzigd =
    verloningsasGewijzigd || nieuw.pauzeFacturatieMinuten !== effectieveOudeFactuurpauze
  const iets = pauzeVeldenGewijzigd(stand, nieuw) || nieuwBetaald !== oudBetaald

  const handmatigWijktAf =
    (verloningsasHandmatig &&
      (stand.pauzeMinutes !== regel.minuten || huidigeVerloning !== regel.verloning)) ||
    (stand.pauzeHandmatigFacturatie &&
      (stand.pauzeFacturatieMinuten ?? -1) !== regel.facturatieMinuten)

  const fo423Betaald = round2(
    urenVoor(input, verloningsasHandmatig ? stand.pauzeMinutes : regel.minuten, 'GESCHREVEN'),
  )

  let soort: HerrekenSoort
  if (input.slot !== 'OPEN') {
    // Gesloten: nooit aanraken. Alleen een geld-verschil is een correctie.
    soort = geldVerschil ? 'CORRECTIELIJST' : 'GEEN_WIJZIGING'
  } else if (vlagHerbeoordeling === 'ONZEKER' && handmatigWijktAf) {
    // Niet aanraken tot de PO over de oude formulier-vlaggen beslist.
    soort = 'HANDMATIG_ONZEKER'
  } else if (iets) {
    soort = 'WIJZIGING'
  } else {
    soort = handmatigWijktAf ? 'HANDMATIG_BEHOUDEN' : 'GEEN_WIJZIGING'
  }

  return {
    soort,
    nieuw,
    nieuweHours: nieuwBetaald,
    oudBetaald,
    nieuwBetaald,
    oudFactuur,
    nieuwFactuur,
    alleenHerkomst: soort === 'WIJZIGING' && !waardenGewijzigd && !geldVerschil,
    vlagHerbeoordeling,
    handmatigWijktAf,
    fo423Betaald,
    facturatieWachtOpBevestiging,
  }
}

// ─── De pauzes van het oude aanmaakformulier ───────────────────────────────

/** Het werkschema zoals het oude aanmaakformulier het zag (vóór de FO-473-migratie). */
export interface OudFormulierWerkschema {
  diensten?: unknown
  shiftType?: string | null
  facturatiePauzeMinuten?: number | null
  standaardPauzeMinuten?: number | null
}

/**
 * FO-473 (review batch-63) - de pauzes die het OUDE aanmaakformulier (batch-56
 * tot FO-473) voor een werkbon op dit werkschema kon meesturen, en die daardoor
 * een handmatig-vlag kregen zonder dat iemand iets koos:
 *
 *   - de standaard `60` van het formulier (ook zonder werkschema);
 *   - de waarde die het formulier na de werkschema-keuze invulde: de pauze van
 *     de EERSTE dienst, zoals de oude create-context die resolvede (dienst ->
 *     korte dienst 0 -> oude facturatie-pauze 0 -> klant-standaard ->
 *     systeemstandaard, geklemd op de dienstduur);
 *   - bij een ploegen-reeks kreeg elke dag de vlag, ook als de eigen pauze van
 *     de dienst won: die dienstpauzes tellen dus ook mee;
 *   - beide systeemstandaarden (dag en ploeg, ook geklemd op de dienstduur):
 *     het schema kan na de migratie gewijzigd zijn (ander ploegsysteem, een
 *     inmiddels ingevulde pauze), en te veel waarden is veilig - die dagen
 *     wachten op akkoord van de PO, te weinig laat een formuliervlag als
 *     "echte keuze" staan.
 *
 * Een handmatig-vlag met een andere waarde (bv. een monteur die 0 boekte) is een
 * echte keuze en wordt niet als "oud formulier" gezien.
 */
export function oudeFormulierPauzes(
  schema: OudFormulierWerkschema | null | undefined,
  systeemDefaults?: Partial<PauzeSysteemDefaults> | null,
): number[] {
  const waarden = new Set<number>([60])
  if (!schema) return [...waarden]
  const diensten = Array.isArray(schema.diensten)
    ? (schema.diensten as unknown[]).filter(
        (d): d is Record<string, unknown> => !!d && typeof d === 'object',
      )
    : []
  const duurVan = (d: Record<string, unknown>): number | null =>
    typeof d.startTime === 'string' && typeof d.endTime === 'string'
      ? dienstduurMinuten(d.startTime, d.endTime)
      : null
  const klem = (minuten: number, duur: number | null): number =>
    duur != null && duur > 0 ? Math.min(minuten, duur) : minuten

  for (const d of diensten) {
    if (bruikbaarPauzeGetal(d.breakMinutes)) {
      waarden.add(d.breakMinutes)
      waarden.add(klem(d.breakMinutes, duurVan(d)))
    }
  }
  const systeemDag = systeemDefaults?.dagdienstMinuten ?? PAUZE_SYSTEEM_DEFAULTS.dagdienstMinuten
  const systeemPloeg = systeemDefaults?.ploegenMinuten ?? PAUZE_SYSTEEM_DEFAULTS.ploegenMinuten
  const duurEerste = diensten[0] ? duurVan(diensten[0]) : null
  for (const systeem of [systeemDag, systeemPloeg]) {
    waarden.add(systeem)
    waarden.add(klem(systeem, duurEerste))
  }

  const eerste = diensten[0]
  if (eerste) {
    const duur = duurVan(eerste)
    if (bruikbaarPauzeGetal(eerste.breakMinutes)) {
      waarden.add(klem(eerste.breakMinutes, duur))
    } else if (duur != null && duur > 0 && duur < KORTE_DIENST_DREMPEL_MINUTEN) {
      waarden.add(0)
    } else if ((schema.facturatiePauzeMinuten ?? 0) > 0) {
      waarden.add(0)
    } else if (bruikbaarPauzeGetal(schema.standaardPauzeMinuten)) {
      waarden.add(klem(schema.standaardPauzeMinuten, duur))
    } else {
      const dag = schema.shiftType === 'DAG'
      const systeem = dag
        ? (systeemDefaults?.dagdienstMinuten ?? PAUZE_SYSTEEM_DEFAULTS.dagdienstMinuten)
        : (systeemDefaults?.ploegenMinuten ?? PAUZE_SYSTEEM_DEFAULTS.ploegenMinuten)
      waarden.add(klem(systeem, duur))
    }
  }
  return [...waarden].sort((a, b) => a - b)
}

// ─── "Terug naar de afspraak" per as (backoffice) ──────────────────────────

export type PauzeHerstelAs = 'VERLONING' | 'FACTURATIE' | 'BEIDE'

export interface HerstelPauzeAsInput {
  stand: DagregelPauzeStand
  /**
   * De cascade voor deze dag zonder handmatige invoer. FO-511: de facturatie-as
   * daarvan is de klantregel, onafhankelijk van de verloningsas.
   */
  regel: PauzeResolutie
  as: PauzeHerstelAs
}

/**
 * Zet één as van een dagregel terug naar de afspraak en haalt de
 * handmatig-vlag van die as weg (spec §B7):
 *
 *   - VERLONING: pauze en verloningskeuze volgen de regel. FO-511: de
 *     factuurpauze blijft zoals hij staat - de assen zijn onafhankelijk;
 *   - FACTURATIE: alleen de factuurpauze volgt de klantregel. De verloningsas -
 *     en dus de betaalde uren - blijft onaangeroerd, ook als die van de regel
 *     afwijkt. De reden van een handmatige factuurpauze vervalt;
 *   - BEIDE: beide assen volgen de regel.
 *
 * Een dagregel van vóór FO-473 zonder eigen factuurpauze houdt bij VERLONING de
 * factuurpauze waarmee hij gefactureerd werd (de afgeleide waarde van de oude
 * stand), zodat de nieuwe verloningsas de factuur niet raakt.
 */
export function herstelPauzeAs(input: HerstelPauzeAsInput): DagregelPauzeVelden {
  const { stand, regel, as } = input
  const huidigeVerloning = stand.pauzeVerloning ?? 'GESCHREVEN'
  const verloningsasTerug = as !== 'FACTURATIE'
  const facturatieasTerug = as !== 'VERLONING'

  const verloningsas = verloningsasTerug
    ? {
        pauzeMinutes: regel.minuten,
        pauzeVerloning: regel.verloning,
        pauzeBron: regel.bron,
        pauzeVerloningBron: regel.verloningBron,
        pauzeHandmatig: false,
      }
    : {
        pauzeMinutes: stand.pauzeMinutes,
        pauzeVerloning: huidigeVerloning,
        pauzeBron: stand.pauzeBron ?? 'MIGRATIE',
        pauzeVerloningBron: stand.pauzeVerloningBron ?? 'MIGRATIE',
        pauzeHandmatig: stand.pauzeHandmatig,
      }

  const facturatieas = facturatieasTerug
    ? {
        pauzeFacturatieMinuten: regel.facturatieMinuten,
        pauzeFacturatieBron: regel.facturatieBron,
        pauzeHandmatigFacturatie: false,
        pauzeFacturatieReden: null,
      }
    : {
        pauzeFacturatieMinuten:
          stand.pauzeFacturatieMinuten ??
          afgeleideFacturatieMinuten(stand.pauzeMinutes, huidigeVerloning),
        pauzeFacturatieBron: stand.pauzeFacturatieBron ?? 'MIGRATIE',
        pauzeHandmatigFacturatie: stand.pauzeHandmatigFacturatie,
        pauzeFacturatieReden: stand.pauzeFacturatieReden ?? null,
      }

  return { ...verloningsas, ...facturatieas }
}

// ─── FO-511 (batch-69) - alleen de facturatie-as herrekenen ─────────────────

/**
 * De sloten die de FACTURATIE-as dichtzetten: gefactureerd, in een
 * facturatieverzoek, in een lopend facturatie-overzicht of een gegeven of lopend
 * klantakkoord (integrator-review M2: de klant accordeert de factuur-uren). De
 * api bepaalt ze met één functie voor de gate, de herrekening en per dag
 * aanpassen (`klantpauzeFacturatieSlot`). De sloten van de
 * verloningsas (verloond, payroll-lock, goedkeuring, dag gesloten) raken de
 * factuurpauze niet: die zijn hier geen reden om niet te herrekenen.
 */
export type FacturatieAsSlot = Extract<
  HerrekenSlot,
  'OPEN' | 'GEFACTUREERD' | 'FACTURATIEVERZOEK' | 'FACTURATIE_OVERZICHT' | 'KLANTAKKOORD'
>

/** De kolommen van de facturatie-as - en NIETS van de verloningsas. */
export type FacturatieAsVelden = Pick<
  DagregelPauzeVelden,
  'pauzeFacturatieMinuten' | 'pauzeFacturatieBron' | 'pauzeHandmatigFacturatie' | 'pauzeFacturatieReden'
>

export interface HerrekenFacturatieAsInput {
  stand: DagregelPauzeStand
  /** Opgeslagen betaalde uren (blijven ongemoeid). */
  hours: number
  datum: Date | string
  /** De klantregel voor deze dag (`resolveFacturatiePauze`). */
  klantregel: { minuten: number; bron: PauzeResolutie['facturatieBron']; bevestigd: boolean }
  slot: FacturatieAsSlot
  /** Het vangnet waarmee een dagregel zonder eigen factuurpauze gefactureerd werd. */
  oudeFacturatiePauze?: OudeFacturatiePauze | null
}

export interface HerrekenFacturatieAsUitkomst {
  /**
   * WACHT_OP_BEVESTIGING: de factuurpauze wijkt af van een klantregel die nog
   * niet bevestigd is (of alleen het systeemvoorstel is). Niet geschreven, wel
   * gerapporteerd - ook bij een gesloten dagregel (pas een bevestigde regel is
   * een correctievoorstel).
   */
  soort: 'GEEN_WIJZIGING' | 'WIJZIGING' | 'CORRECTIELIJST' | 'HANDMATIG_BEHOUDEN' | 'WACHT_OP_BEVESTIGING'
  /** Alleen de facturatie-as; de verloningsas en `hours` horen hier bewust niet bij. */
  nieuw: FacturatieAsVelden
  /** De factuurpauze waarmee de dagregel tot nu toe gefactureerd werd. */
  oudFactuurpauze: number
  oudFactuur: number
  nieuwFactuur: number
  /** Wijzigt er alleen herkomst (zelfde minuten, zelfde factuur-uren)? */
  alleenHerkomst: boolean
}

/**
 * FO-511 §D9 - de herrekening op de FACTURATIE-as alleen: de factuurpauze van
 * een niet-gefactureerde dagregel gaat naar de klantregel, ongeacht
 * `pauzeHandmatig` (die vlag betreft de verloningsas). Een handmatige
 * factuurpauze (kantoor, met reden) blijft staan. Een dagregel die al
 * gefactureerd is, in een facturatieverzoek of in een overzicht zit, wordt nooit
 * aangeraakt: wijkt de klantregel af, dan komt hij op de correctielijst
 * (creditering of herfacturatie is een beslispunt van de PO).
 *
 * De uitkomst kent geen verloningsvelden: deze functie KAN de betaalde uren niet
 * veranderen.
 */
export function herrekenFacturatieAs(input: HerrekenFacturatieAsInput): HerrekenFacturatieAsUitkomst {
  const { stand, klantregel } = input
  const huidigeVerloning = stand.pauzeVerloning ?? 'GESCHREVEN'
  const oude = input.oudeFacturatiePauze ?? null
  const vangnetMinuten =
    stand.pauzeFacturatieMinuten == null ? vangnetFactuurpauzeMinuten(oude, input.datum, input.hours) : 0
  const oudFactuurpauze =
    stand.pauzeFacturatieMinuten ??
    afgeleideFacturatieMinuten(stand.pauzeMinutes, huidigeVerloning) + vangnetMinuten

  const nieuw: FacturatieAsVelden = stand.pauzeHandmatigFacturatie
    ? {
        pauzeFacturatieMinuten: oudFactuurpauze,
        pauzeFacturatieBron: stand.pauzeFacturatieBron ?? 'EXPLICIET',
        pauzeHandmatigFacturatie: true,
        pauzeFacturatieReden: stand.pauzeFacturatieReden ?? null,
      }
    : {
        pauzeFacturatieMinuten: klantregel.minuten,
        pauzeFacturatieBron: klantregel.bron,
        pauzeHandmatigFacturatie: false,
        pauzeFacturatieReden: null,
      }
  const wijktAf = !stand.pauzeHandmatigFacturatie && klantregel.minuten !== oudFactuurpauze

  const oudFactuur = facturabeleUren(
    {
      hours: input.hours,
      date: input.datum,
      pauzeMinutes: stand.pauzeMinutes,
      pauzeVerloning: stand.pauzeVerloning,
      pauzeFacturatieMinuten: stand.pauzeFacturatieMinuten,
    },
    oude,
  )
  const nieuwFactuur = facturabeleUren({
    hours: input.hours,
    date: input.datum,
    pauzeMinutes: stand.pauzeMinutes,
    pauzeVerloning: stand.pauzeVerloning,
    pauzeFacturatieMinuten: nieuw.pauzeFacturatieMinuten,
  })

  const gewijzigd =
    stand.pauzeFacturatieMinuten !== nieuw.pauzeFacturatieMinuten ||
    stand.pauzeFacturatieBron !== nieuw.pauzeFacturatieBron ||
    stand.pauzeHandmatigFacturatie !== nieuw.pauzeHandmatigFacturatie ||
    (stand.pauzeFacturatieReden ?? null) !== nieuw.pauzeFacturatieReden

  let soort: HerrekenFacturatieAsUitkomst['soort']
  if (!stand.pauzeHandmatigFacturatie && !klantregel.bevestigd) {
    // Nooit schrijven op een aanname; open of gesloten: eerst bevestigen.
    soort = wijktAf || nieuwFactuur !== oudFactuur ? 'WACHT_OP_BEVESTIGING' : 'GEEN_WIJZIGING'
  } else if (input.slot !== 'OPEN') {
    soort = nieuwFactuur !== oudFactuur ? 'CORRECTIELIJST' : 'GEEN_WIJZIGING'
  } else if (stand.pauzeHandmatigFacturatie) {
    soort = oudFactuurpauze !== klantregel.minuten ? 'HANDMATIG_BEHOUDEN' : 'GEEN_WIJZIGING'
  } else {
    soort = gewijzigd ? 'WIJZIGING' : 'GEEN_WIJZIGING'
  }

  return {
    soort,
    nieuw,
    oudFactuurpauze,
    oudFactuur,
    nieuwFactuur,
    alleenHerkomst:
      soort === 'WIJZIGING' &&
      nieuw.pauzeFacturatieMinuten === oudFactuurpauze &&
      nieuwFactuur === oudFactuur,
  }
}
